import { paymentSettingsService } from 'components/api/paymentSettings';
import {
  CatalogueAsset,
  CatalogueDirection,
  CatalogueMethod,
  CatalogueNetwork,
  PaymentSettingCatalogue,
} from 'types/paymentSetting';

/** Direction-specific calls so the views never branch on deposit/withdraw. */
export interface DirectionApi {
  direction: CatalogueDirection;
  list: (siteId: string | number) => Promise<any>;
  toggleMethod: (methodId: number, isEnabled: boolean) => Promise<any>;
  saveMin: (method: CatalogueMethod, value: string) => Promise<any>;
  railBody: (methodId: number) => { depositMethodId?: number; withdrawMethodId?: number };
  minOf: (method: CatalogueMethod) => string | null | undefined;
}

export const makeDirectionApi = (direction: CatalogueDirection): DirectionApi =>
  direction === 'deposit'
    ? {
        direction,
        list: (siteId) => paymentSettingsService.getDepositMethods(siteId),
        toggleMethod: (id, on) => paymentSettingsService.toggleDepositMethod(id, on),
        saveMin: (m, v) =>
          paymentSettingsService.updateDepositMethod(m.id, {
            name: m.name,
            minAmountUsd: v ? v : null,
          }),
        railBody: (id) => ({ depositMethodId: id }),
        minOf: (m) => m.minAmountUsd,
      }
    : {
        direction,
        list: (siteId) => paymentSettingsService.getWithdrawMethods(siteId),
        toggleMethod: (id, on) => paymentSettingsService.toggleWithdrawMethod(id, on),
        saveMin: (m, v) =>
          paymentSettingsService.updateWithdrawMethod(m.id, {
            name: m.name,
            minAmount: v ? v : null,
          }),
        railBody: (id) => ({ withdrawMethodId: id }),
        minOf: (m) => m.minAmount,
      };

/** "Live on this provider": this row serves the network, and network + row are enabled. */
export const isNetworkLive = (n: CatalogueNetwork): boolean =>
  n.servesThisSetting && n.isEnabled && n.method.isEnabled;

/** Why players cannot see anything under this provider/coin even when a network is "live". */
export const hiddenReason = (
  setting: PaymentSettingCatalogue,
  asset: CatalogueAsset,
): string | null =>
  !setting.isEnabled ? 'provider setting disabled' : !asset.isEnabled ? 'coin disabled' : null;

/**
 * Make a network live on this provider. Steps are skipped when already true
 * and ordered so every partial failure is harmless: enable the coin, enable
 * the row (a rail must never point at a disabled row), point the rail at it,
 * enable the network.
 */
export async function setNetworkLive(
  n: CatalogueNetwork,
  api: DirectionApi,
  asset?: CatalogueAsset,
): Promise<void> {
  if (asset && !asset.isEnabled) {
    await paymentSettingsService.toggleAsset(asset.id, true);
  }
  if (!n.method.isEnabled) {
    await api.toggleMethod(n.method.id, true);
  }
  if (!n.servesThisSetting) {
    await paymentSettingsService.setNetworkRail(n.id, api.railBody(n.method.id));
  }
  if (!n.isEnabled) {
    await paymentSettingsService.toggleNetwork(n.id, true);
  }
}

/** This provider stops serving one network: its row is disabled; network and coin stay as they are. */
export async function setNetworkOff(n: CatalogueNetwork, api: DirectionApi): Promise<void> {
  if (n.method.isEnabled) {
    await api.toggleMethod(n.method.id, false);
  }
}

/** Networks of the coin that are live via this provider. */
export const liveNetworks = (asset: CatalogueAsset): CatalogueNetwork[] =>
  asset.networks.filter(isNetworkLive);

/** "This provider serves this coin": at least one network is live via this provider. */
export const isCoinLiveOnProvider = (asset: CatalogueAsset): boolean =>
  liveNetworks(asset).length > 0;

/** Every network of the coin live via this provider (coin enabled first). */
export async function setCoinLive(asset: CatalogueAsset, api: DirectionApi): Promise<void> {
  if (!asset.isEnabled) {
    await paymentSettingsService.toggleAsset(asset.id, true);
  }
  for (const n of asset.networks) {
    await setNetworkLive(n, api);
  }
}

/** This provider stops serving the coin: all of its rows for this coin are disabled. */
export async function setCoinOff(asset: CatalogueAsset, api: DirectionApi): Promise<void> {
  for (const n of asset.networks) {
    await setNetworkOff(n, api);
  }
}

/** Ordered `{ id, sortOrder }` items from an id sequence. */
export const orderItems = (ids: number[]) => ids.map((id, sortOrder) => ({ id, sortOrder }));
